import { apiClient } from '@/lib/api-client'

interface PowerFields {
  deviceType?: string | null
  pluginType?: string | null
  chargerAvailable?: boolean | null
  chargerId?: string | null
  batteryType?: string | null
  batteryCount?: number | null
}

export function typeLabel(item: PowerFields) {
  if (!item?.deviceType) return '—'
  if (item.deviceType === 'plugin') return 'Plugin'
  if (item.deviceType === 'chargeable') return 'Chargeable'
  if (item.deviceType === 'single-use') return 'Single use'
  return 'Non-chargeable'
}

export function powerFieldLabel(item: PowerFields) {
  if (item.deviceType === 'non-chargeable') return 'Batteries'
  if (item.deviceType === 'single-use') return 'Battery'
  if (item.deviceType === 'chargeable') return 'Charger'
  return 'Connection'
}

export function powerDetail(item: PowerFields) {
  if (item.deviceType === 'plugin') {
    return item.pluginType === 'usb' ? 'USB' : item.pluginType === 'wall_socket' ? 'Wall socket' : '—'
  }
  if (item.deviceType === 'chargeable') {
    if (!item.chargerAvailable) return 'No charger'
    return item.chargerId ? `Charger ${item.chargerId}` : 'Charger included'
  }
  if (item.deviceType === 'non-chargeable') {
    return item.batteryType ? `${item.batteryCount || 1} × ${item.batteryType}` : '—'
  }
  if (item.deviceType === 'single-use') {
    return 'Inbuilt battery'
  }
  return '—'
}

export function assetStatusRank(status?: string) {
  switch (status) {
    case 'assigned_pending':
      return 0
    case 'available':
      return 1
    case 'assigned':
      return 2
    case 'broken':
      return 3
    case 'unavailable':
      return 4
    case 'completely_used':
      return 5
    case 'eol':
      return 6
    default:
      return 7
  }
}

export function compareAssets(
  left: { status?: string; serialNumber?: string | null },
  right: { status?: string; serialNumber?: string | null },
) {
  const byStatus = assetStatusRank(left.status) - assetStatusRank(right.status)
  if (byStatus) return byStatus
  return String(left.serialNumber || '').localeCompare(String(right.serialNumber || ''), undefined, {
    numeric: true,
    sensitivity: 'base',
  })
}

export function statusLabel(status?: string) {
  switch (status) {
    case 'available':
      return 'Available'
    case 'assigned':
      return 'Assigned'
    case 'assigned_pending':
      return 'Pending approval'
    case 'unavailable':
      return 'Unavailable'
    case 'broken':
      return 'Broken'
    case 'completely_used':
      return 'Completely used'
    case 'eol':
      return 'EoL'
    default:
      return status || '—'
  }
}

export function ownershipLabel(asset: { ownershipType?: string; project?: { name?: string } | null }) {
  if (asset.ownershipType === 'project') return asset.project?.name || 'Project'
  return 'Company'
}

export function canAssign(status?: string) {
  return status === 'available' || status === 'assigned' || status === 'assigned_pending'
}

export function canReturn(status?: string) {
  return status === 'assigned' || status === 'assigned_pending'
}

export async function returnAsset(deviceId: number) {
  const assignment = await getActiveAssignment(deviceId)
  if (!assignment) {
    throw new Error('No active assignment was found for this asset')
  }
  await apiClient.put(`/assignments/${assignment.id}/return`)
}

export async function getActiveAssignment(deviceId: number) {
  const response = await apiClient.get('/assignments', {
    params: { deviceId, status: 'active' },
  })
  return (response.data || []).find((item: any) => !item.returnedOn) || null
}
